/**
 * SubJobsDisplay Component
 * Shows parent job with expandable sub-jobs in a clean, intuitive format
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Briefcase, Users } from 'lucide-react';
import { Badge } from './ui/badge';

const SubJobsDisplay = ({ parentJob, onJobSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const [subJobs, setSubJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smartStatus, setSmartStatus] = useState(null);

  useEffect(() => {
    if (expanded && parentJob.is_parent_job) {
      fetchSubJobs();
      fetchSmartStatus();
    }
  }, [expanded, parentJob.id]);

  const fetchSubJobs = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/jobs/${parentJob.id}/sub-jobs`);
      
      if (response.ok) {
        const data = await response.json();
        setSubJobs(data.sub_jobs || []);
      }
    } catch (error) {
      console.error('Error fetching sub-jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmartStatus = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/jobs/${parentJob.id}/smart-status`);
      
      if (response.ok) {
        const data = await response.json();
        setSmartStatus(data.smart_status);
      }
    } catch (error) {
      console.error('Error fetching smart status:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'accepting_candidates': 'bg-green-100 text-green-800',
      'filled': 'bg-blue-100 text-blue-800',
      'on_hold': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800',
      'interviewing': 'bg-purple-100 text-purple-800',
      'offer_out': 'bg-indigo-100 text-indigo-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const isParentJob = parentJob.is_parent_job;

  return (
    <div className="border-l-4 border-blue-500" data-testid="subjobs-display">
      {/* Parent Job Row */}
      <div 
        className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${isParentJob ? 'bg-blue-50' : ''}`}
        onClick={() => {
          if (isParentJob) {
            setExpanded(!expanded);
          } else {
            onJobSelect?.(parentJob);
          }
        }}
        data-testid="parent-job-row"
      >
        {isParentJob && (
          <button 
            className="text-gray-500 hover:text-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            data-testid="expand-toggle"
          >
            {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
        )}
        
        <Briefcase className="h-5 w-5 text-blue-600" />
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{parentJob.title}</span>
            {isParentJob && (
              <>
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {parentJob.no_of_openings} Positions
                </Badge>
                {smartStatus && (
                  <Badge className={getStatusColor(parentJob.status)}>
                    {smartStatus}
                  </Badge>
                )}
              </>
            )}
            {!isParentJob && (
              <Badge className={getStatusColor(parentJob.status)}>
                {parentJob.status?.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {parentJob.location} • {parentJob.employment_type || 'Full-time'}
          </p>
        </div>
      </div>

      {/* Sub-Jobs (Expandable) */}
      {isParentJob && expanded && (
        <div className="ml-10 border-l-2 border-gray-200" data-testid="subjobs-list">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">Loading sub-jobs...</div>
          ) : subJobs.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No sub-jobs found</div>
          ) : (
            subJobs.map((subJob, index) => (
              <div
                key={subJob.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => onJobSelect?.(subJob)}
                data-testid={`subjob-row-${index}`}
              >
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">
                  {subJob.sub_job_index}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {subJob.sub_job_index} of {subJob.total_sub_jobs}
                    </span>
                    <Badge className={getStatusColor(subJob.status)} variant="outline">
                      {subJob.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Sub-job #{subJob.sub_job_index}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SubJobsDisplay;
